/* eslint-disable prefer-arrow-callback */
/* eslint-disable no-param-reassign */
import { format } from 'date-fns'
import {
    GridColDef,
    GridRowClassNameParams,
    GridRowId,
    GridRowParams,
    GridSlotsComponent,
} from '@mui/x-data-grid'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
    Box,
    Checkbox,
    Chip,
    Grid,
    IconButton,
    Link,
    styled,
    TextField,
    useMediaQuery,
} from '@mui/material'
import { shallowEqual, useSelector } from 'react-redux'
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF, useGridApiRef } from '@mui/x-data-grid-pro'
import { useNavigate, useSearchParams } from 'react-router-dom'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'
import { localeDateFormat } from '../../utils/localeUtils'
import { hideDrawer, hideHeaderDrawer, setSelectedDepartment, showDrawer } from '../../redux/reducers/appSettingsReducer'
import {
    setSelectedDepartmentFilter,
    setFilterStates,
    setEnhancementsOnlyToggle,
    setCheckboxCount,
} from '../../redux/reducers/myActionsReducer'
import {
    showErrorMessage,
    showInfoMessage,
    showSuccessMessage,
} from '../../redux/reducers/snackbarReducer'
import { RootStore, useAppDispatch } from '../../redux/store'
import { SelectOption } from '../../services/dashboardService'
import { manualRequestsService } from '../../services/myActionsService'

import LoadingIndicator from '../../shared/UI/LoadingIndicator'
import NoResult from '../../shared/UI/NoResult'

import StatusLabel from '../../shared/UI/StatusLabel'
import TypeLabel from '../../shared/UI/TypeLabel'
import Card from '../../shared/layout/Card'
import { isMobileDown } from '../../theme/deviceChecks'
import { getPalleteTypeByProp } from '../../theme/palette'
import theme from '../../theme/theme'
import { BaseResponse } from '../../types/base-response'
import { getSelectValuesByType, getFilteredDepartmentsDropdownOptions } from '../../utils/app-utils'
import { normaliseForComparison } from '../../utils/name-utils'
import { getLocalDateString } from '../../utils/date-utils'
import UserErrorMessage from '../../utils/errorFilter'
import Alert from '../../shared/UI/Alert/Alert'
import { CallOutSummary } from '../CallOutSummary/CallOutSummary'
import { RowExpansionToggle } from './RowExpansionToggle'
import { operationTypes, OperationType, enhancementTypes } from '../../utils/constants'
import {
    BulkSubmitRequest,
    BulkSubmitResponse,
    SubmitStatus,
} from '../../models/enhancement'
import { v2MyActionsService } from '../../services/myActionsServiceV2'
import {
    SytledMyActionsGrid,
    approveButtonStyles,
    bulkActionsContainer,
    countChipStyles,
    declineButtonStyles,
} from './StyledMyActionsGrid'
import { resolveToPath } from './pathResolver'
import Modal from '../../shared/UI/Modal'
import ButtonSmall from '../../shared/UI/ButtonSmall'
import { isEnhancement } from '../../utils/SharedMethods/isEnhancement'
import CustomGridFooter from './CustomGridFooter'
import { FlattenedRequest } from '../../types/flattened-requests'
import { OpenAbsenceResponse } from '../../types/open-absence'
import MyActionsFilter from '../MyActions/MyActionsFilter/MyActionsFilter'
import { CheckboxFilterSettings, SelectionList } from '../MyActions/MyActionsFilter/Types'
import FilterChips from '../MyActions/MyActionsFilter/FilterChips'
import {
    filter,
    getInitialCheckboxSettings,
    getInitialRequestParams,
    getInitialSelectionStates,
} from './myactionsFlitering'
import { MOBILE_EXCLUDED_REQUEST_TYPES } from './Shared/enums'
import { getRequestStatusDisplayName } from '../../utils/SharedMethods/getRequestStatusDisplayName'
import { removeTrailingZeroes } from '../../utils/conversion'
import WfmAbsenceActionDrawer from '../AbsenceDrawerComponent/WfmAbsenceDrawer'
import Drawer from '../../shared/UI/Drawer'
import actionsService from '../../services/actionsService'
import { tryAgainLater } from '../AbsenceDrawerComponent/WfmAbsenceDrawer/Hooks/useWfmAbsence'
import OpenAbsences from '../Absence/OpenAbsence/OpenAbsence'

function NoRowsOverlay() {
    return <NoResult message="No Actions" showImage />
}

function LoadingOverlay() {
    return <LoadingIndicator show alignItems="center" />
}

function headerText(testId: string, headerName?: string) {
    return (
        <div style={{ fontWeight: 'bold', color: 'black' }} data-testid={testId}>
            {headerName}
        </div>
    )
}

const getSelectedRequestTypes = (checkboxSettings: CheckboxFilterSettings[]) =>
    checkboxSettings.filter(setting => setting.checked)

export function MyActionsGrid() {
    const apiRef = useGridApiRef()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [pageSize, setPageSize] = useState<number>(10)

    const [rerenderCheckBox, setRerenderCheckBox] = useState<number>(1)
    const [rerenderDataGrid, setRerender] = useState<number>(1)
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [doesDisplayInfoMessage, setDoesDisplayInfoMessage] = useState<boolean>(true)
    const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState<GridRowId[]>([])
    const [wfmAbsencePlaceholder, setWfmAbsencePlaceholder] = useState<FlattenedRequest>()
    const mobile = useMediaQuery(isMobileDown())
    const [bulkSumitDeclineRequestIds, setBulkSumitDeclineRequestIds] = useState<number[]>([])
    const [bulkSumitApproveRequestIds, setBulkSumitApproveRequestIds] = useState<number[]>([])
    const [headerChecked, setHeaderChecked] = useState<boolean>(false)
    const [requestsArray, setRequestsArray] = useState<FlattenedRequest[]>([])
    const [comments, setComments] = useState<string>('')
    const [commentsError, setCommentsError] = useState<boolean>(false)
    const [openAbsences, setOpenAbsences] = useState<OpenAbsenceResponse[]>([])
    const { selectedDepartmentFilter } = useSelector((state: RootStore) => state.myActions)

    const checkboxSettings = useSelector(
        (state: RootStore) => state.myActions.filterStates.checkboxSettings,
        shallowEqual
    )
    const requestParams = useSelector(
        (state: RootStore) => state.myActions.filterStates.requestParams,
        shallowEqual
    )
    const selectionStateEmployees = useSelector(
        (state: RootStore) => state.myActions.filterStates.selectionState.employees,
        shallowEqual
    )
    const enhancementsOnly = useSelector(
        (state: RootStore) => state.myActions.filterStates.requestParams.enhancementsOnly
    )

    const metaData = useSelector((state: RootStore) => state.myActions.filterStates.metaData)

    const { employeeDetails } = useSelector((state: RootStore) => state.appSettings)

    interface ModalState {
        message: string
        operation: OperationType
        requestIds: number[]
    }

    const modalState: ModalState = {
        message: '',
        operation: operationTypes.APPROVE,
        requestIds: [],
    }

    const [showModal, setShowModal] = useState(false)
    const [isModalLoading, setIsModalLoading] = useState(false)
    const [showOpenAbsenceModal, setOpenAbsenceModal] = useState(false)

    const modalStateRef = useRef(modalState)

  const {
    departments,
    allEmployees,
    selectedDepartment,
    allTeams,
    secondaryApproverEmployees,
    leaverEmployees,
  } = useSelector((state: RootStore) => state.appSettings)

    const showSideDrawer = useSelector<RootStore, boolean>(
        (state: RootStore) => state.appSettings.showDrawer
    )

    const dropdownDepartments = useMemo(
        () => getFilteredDepartmentsDropdownOptions(departments, allEmployees),
        [allEmployees, departments]
    )
    const userDepartment = useSelector<RootStore, SelectOption | null>(
        (state: RootStore) => state.appSettings.loggedInEmployeeDepartment
    )

    const { myActionsV3 } = useSelector((state: RootStore) => state.featureToggles)
    const { userSettings } = useSelector((state: RootStore) => state.appSettings)
    const enhancementsSettingOn = useMemo(() => userSettings?.hasEnhancements, [userSettings])
    const { currentEntitlementPeriodResponse } = useSelector((state: RootStore) => state.appSettings)
    const { calendarDetailsResponse } = useSelector((state: RootStore) => state.appSettings)
    const requestStatusResult = getSelectValuesByType('RequestStatus')
    const unallocated = 'unallocated'
    const shouldSelectAllOption = useCallback(
        (): boolean =>
            userDepartment?.displayValue.toLowerCase().trim() === unallocated || departments.length > 1,
        [userDepartment?.displayValue, departments.length]
    )

    const selectionLists = useRef<SelectionList>({
        departments: [],
        teams: {},
        employees: [],
        statuses: [{ value: 0, displayValue: 'All' }],
        directReportees: true,
    })

    // track the number of bulk decline requests that are call outs.
    const callOutDeclinedCount = useRef<number>(0)
    // track if single decline request is of type call out
    const hasCallOutDeclined = useRef<boolean>(false)
    // check if user clicked on single or bulk decline
    const declinedTriggeredBy = useRef<string>('')

    const filteredRequests = useMemo(() => {
        const requests = requestsArray

        return filter(mobile, checkboxSettings, requests)
    }, [requestsArray, checkboxSettings, mobile])

    // Count the number of items across filter types (for the filter badge)
    // On mobile, exclude certain request types from the count
    const getFilterTypeCount = useCallback(() => {
        if (mobile) {
            return checkboxSettings
                .filter(setting => !MOBILE_EXCLUDED_REQUEST_TYPES.includes(setting.type))
                .reduce((sum, setting) => sum + (setting.count || 0), 0)
        }
        return checkboxSettings.reduce((sum, setting) => sum + (setting.count || 0), 0)
    }, [checkboxSettings, mobile])

    // if all filters have been removed, and we only fetched enhancements so far, we need to fetch more data
    // before we can render the table contents
    const isMissingData =
        getSelectedRequestTypes(checkboxSettings).length === 0 && requestParams.enhancementsOnly
    const showLoadingSpinner = isLoading || isMissingData

    const [searchParams] = useSearchParams()

    useEffect(() => {
        setHeaderChecked(false)
        setBulkSumitDeclineRequestIds([])
        setBulkSumitApproveRequestIds([])
    }, [filteredRequests])

    useEffect(() => {
        selectionLists.current.directReportees = true
        selectionLists.current.employees = allEmployees
        if (departments) {
            departments.forEach(department => {
                const teams =
                    department?.value !== 0
                        ? allTeams.filter(team => team.departmentId === department.value)
                        : []
                selectionLists.current.teams[department.value] = teams
            })
            selectionLists.current.departments = dropdownDepartments
        }

        if (requestStatusResult.length) {
            selectionLists.current.statuses = requestStatusResult
        }
    }, [
        allTeams,
        departments,
        selectedDepartment?.value,
        selectedDepartmentFilter?.value,
        requestStatusResult,
        allEmployees,
        dropdownDepartments,
    ])

    const fetchRequests = useCallback(async () => {
        if (myActionsV3 === undefined) {
            return
        }

        setIsLoading(true)
        if (!requestParams || enhancementsSettingOn === undefined) return
        const {
            departmentId,
            departmentTeamId,
            directReportees,
            dateFrom,
            dateTo,
            requestStatusId,
            employeeIds,
        } = requestParams

        interface RequestsResponse {
            requests: FlattenedRequest[]
        }
        let getRequests: Promise<RequestsResponse>

        if (myActionsV3) {
            getRequests = actionsService.myActions.getManagerActions(requestParams)
        } else if (enhancementsSettingOn) {
            getRequests = v2MyActionsService.getRequests(requestParams)
        } else {
            getRequests = manualRequestsService.getRequests(
                directReportees,
                dateFrom,
                dateTo,
                departmentId,
                departmentTeamId,
                employeeIds,
                requestStatusId
            )
        }

        await getRequests
            .then(deptTeams => {
                let { requests } = deptTeams
                const selectedEmployeeNames =
                    selectionStateEmployees?.map(emp => normaliseForComparison(emp.employeeName)) || []
                if (selectedEmployeeNames.length > 0) {
                    requests = requests.filter(
                        request =>
                            request.displayName &&
                            selectedEmployeeNames.includes(normaliseForComparison(request.displayName))
                    )
                }

        if (departmentId && departmentId !== 0) {
          const validNames = new Set([
            ...allEmployees
              .filter(e => e.departmentId === departmentId)
              .map(e => normaliseForComparison(e.employeeName)),
            ...secondaryApproverEmployees
              .filter(e => e.departmentId === departmentId)
              .map(e => normaliseForComparison(e.employeeName)),
            ...leaverEmployees
              .filter(e => e.departmentId === departmentId)
              .map(e => normaliseForComparison(e.employeeName)),
          ])

                    requests = requests.filter(
                        r => r.displayName && validNames.has(normaliseForComparison(r.displayName))
                    )
                }

        setRequestsArray(requests)
        dispatch(setCheckboxCount(deptTeams.requests))
        setIsLoading(false)
      })
      .catch(err => {
        const response: BaseResponse = err.response?.data
        response.errors.forEach(error => {
          dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
        })
        setIsLoading(false)
      })
  }, [
    enhancementsSettingOn,
    requestParams,
    dispatch,
    myActionsV3,
    allEmployees,
    secondaryApproverEmployees,
    selectionStateEmployees,
    leaverEmployees,
  ])

    useEffect(() => {
        if (metaData.initialised) {
            fetchRequests()
        }
    }, [metaData.initialised, fetchRequests])

    useEffect(() => {
        if (requestParams.enhancementsOnly && getSelectedRequestTypes(checkboxSettings).length === 0) {
            setIsLoading(true)
            dispatch(setEnhancementsOnlyToggle(false))
        }
    }, [requestParams, checkboxSettings, dispatch])

    useEffect(() => {
        const isNotInitialisedAndAllRequiredValuesPresent =
            !metaData.initialised &&
            currentEntitlementPeriodResponse &&
            calendarDetailsResponse &&
            (selectedDepartmentFilter?.value || selectedDepartmentFilter?.value === 0) &&
            enhancementsSettingOn !== undefined

        if (isNotInitialisedAndAllRequiredValuesPresent) {
            const initialRequestParams = getInitialRequestParams(
                calendarDetailsResponse,
                selectedDepartmentFilter,
                currentEntitlementPeriodResponse
            )

            const selectionState = getInitialSelectionStates(
                calendarDetailsResponse,
                selectedDepartmentFilter,
                currentEntitlementPeriodResponse
            )

            if (initialRequestParams && selectionState) {
                dispatch(
                    setFilterStates({
                        requestParams: initialRequestParams,
                        selectionState,
                        checkboxSettings: getInitialCheckboxSettings(enhancementsSettingOn),
                        requestParamsDefaults: { ...initialRequestParams },
                        selectionStateDefault: { ...selectionState },
                        metaData: { initialised: true },
                    })
                )
            }
        }
    }, [
        currentEntitlementPeriodResponse,
        calendarDetailsResponse,
        selectedDepartmentFilter?.value,
        enhancementsSettingOn,
        requestsArray,
        selectedDepartmentFilter,
        metaData.initialised,
        dispatch,
    ])

    useEffect(
        function initialiseDepartmentSelection() {
            const toBeSelected =
                (shouldSelectAllOption()
                    ? dropdownDepartments.find(dep => dep.value === 0)
                    : userDepartment) ?? null

            dispatch(setSelectedDepartmentFilter(toBeSelected))
            dispatch(setSelectedDepartment(toBeSelected))
        },
        [departments, dispatch, dropdownDepartments, shouldSelectAllOption, userDepartment]
    )

    const calculateStatusLabel = (row: any) => {
        if (row.isCancellation) return 'Cancellation Requested'
        if (!row.statusDescription) return
        if (row.isQueried) return 'Queried'
        return getRequestStatusDisplayName(row.statusDescription)
    }

    const updateBulkApproveDeclineArrays = (
        isChecked: boolean,
        id: number,
        statusDescription: string,
        requestType: string
    ) => {
        const addToArrays = () => {
            bulkSumitDeclineRequestIds.push(id)
            if (statusDescription === SubmitStatus.PENDING) bulkSumitApproveRequestIds.push(id)
        }

        const removeFromArray = (requestId: number, array: number[]) => {
            const index = array.indexOf(requestId)
            if (index > -1) {
                array.splice(index, 1)
            }
        }

        const updateCallOutCount = (value: number) => {
            if (requestType === 'Call Out') {
                callOutDeclinedCount.current += value
            }
        }

        if (isChecked) {
            addToArrays()
            updateCallOutCount(1)
        } else {
            removeFromArray(id, bulkSumitDeclineRequestIds)
            removeFromArray(id, bulkSumitApproveRequestIds)
            updateCallOutCount(-1)
        }
    }

    const isChecked = (id: number): boolean => bulkSumitDeclineRequestIds.includes(id)
    const canApprove = (status: string, requestType: string) => {
        if (isEnhancement(requestType)) {
            return SubmitStatus.PENDING === status
        }

        return false
    }
    const canDecline = (status: string, requestType: string): boolean => {
        const declineStatuses: string[] = [
            SubmitStatus.AMENDED,
            SubmitStatus.APPROVED,
            SubmitStatus.PENDING,
        ]

        if (isEnhancement(requestType)) {
            return declineStatuses.includes(status)
        }

        return false
    }
    const handleSuccess = async (response: BulkSubmitResponse, approvedDeclined: string) => {
        setBulkSumitDeclineRequestIds([])
        setBulkSumitApproveRequestIds([])
        if (checkboxSettings) await fetchRequests()
        const quantityOfRequests = response.requestIds?.length
        const message = quantityOfRequests === 1 ? 'request has been' : 'requests have been'
        dispatch(showSuccessMessage(`${quantityOfRequests} ${message} ${approvedDeclined}`))
        setRerender(rerenderDataGrid + 1)
    }

    const resetModal = () => {
        setComments('')
        setCommentsError(false)
        setShowModal(false)
        setIsModalLoading(false)
    }

    const resetOpenAbsenceModal = () => {
        setOpenAbsences([])
        setOpenAbsenceModal(false)
    }

    const sendRequestsToApprove = (requestIds: number[]) => {
        const request: BulkSubmitRequest = {
            requestIds,
            comments,
        }
        v2MyActionsService
            .postBulkSubmitApprove(request)
            .then(response => handleSuccess(response, `approved`))
            .catch(err => {
                dispatch(showErrorMessage(`The requests have not been approved`))
            })
            .finally(resetModal)
    }

    const sendRequestsToDecline = (requestIds: number[]) => {
        const request: BulkSubmitRequest = {
            requestIds,
            comments,
        }
        v2MyActionsService
            .postBulkSubmitDecline(request)
            .then(response => handleSuccess(response, `declined`))
            .catch(err => {
                dispatch(showErrorMessage(`The requests have not been declined`))
            })
            .finally(resetModal)
    }

    const setModalData = (ids: number[], operation: OperationType) => {
        const isSingleCallOutDeclined =
            hasCallOutDeclined.current && declinedTriggeredBy.current === 'single'
        const isBulkCallOutDeclined =
            callOutDeclinedCount.current > 0 &&
            declinedTriggeredBy.current === 'bulk' &&
            operation === operationTypes.DECLINE

        const modal = modalStateRef.current
        if (isSingleCallOutDeclined || isBulkCallOutDeclined) {
            modal.message = `By declining this request, you confirm that you will review all callouts for this employee to ensure correct payments are made`
        } else {
            modal.message = `Are you sure you would like to ${operation} ${ids.length} request(s)?`
        }
        modal.operation = operation
        modal.requestIds = ids
        hasCallOutDeclined.current = false
    }

    const approveRequests = (ids: number[]) => {
        setModalData(ids, operationTypes.APPROVE)
        setShowModal(true)
    }

    const declineRequests = (ids: number[]) => {
        const row = requestsArray.find(r => r.id === ids[0])

        if (row?.requestType === 'Call Out') {
            hasCallOutDeclined.current = true
        }
        declinedTriggeredBy.current = 'single'
        setModalData(ids, operationTypes.DECLINE)
        setShowModal(true)

    }

    const showDrawerOpenAbsence = () => {
        dispatch(showDrawer())
        resetOpenAbsenceModal()
    }

    const getEmployeeOpenAbsences = (employeeId?: number) => {
        actionsService.employee
            .getOpenAbsencesByEmployeeId(employeeId)
            .then(response => {
                if (response.length >= 1) {
                    setOpenAbsences(response)
                    setOpenAbsenceModal(true)
                } else {
                    // if no other open absences just show the drawer
                    dispatch(showDrawer())
                }
            })
            .catch(err => {
                dispatch(
                    showErrorMessage(
                        `An error occured whilst fetching open absences for employee ${employeeId}`
                    )
                )
            })
    }

    const handleWfmAbsenceClick = (type: string, id: number) => {
        const placeholder = requestsArray.find(x => x.id === id && x.requestType === type)

        if (!placeholder) {
            return
        }

        if (placeholder.statusDescription?.toLowerCase() === 'actioned') {
            return
        }

        const employeeId = allEmployees.find(
            e => normaliseForComparison(e.employeeName) === normaliseForComparison(placeholder?.displayName ?? '')
        )?.employeeId

        actionsService.wfm
            .tryGetLockForWfmAbsencePlaceholder(placeholder.id)
            .then(response => {
                if (response.result) {
                    setWfmAbsencePlaceholder(placeholder)
                    getEmployeeOpenAbsences(employeeId)
                    return
                }

                const employee = placeholder.displayName
                const from = format(new Date(placeholder.dateFrom), localeDateFormat)
                const errMsg = `Wfm Absence for ${employee} starting ${from} is currently being actioned by ${response.lockedBy}.`
                dispatch(showInfoMessage(errMsg))
            })
            .catch(err => {
                dispatch(showErrorMessage(`Wfm Absence could not be locked. ${tryAgainLater}`))
            })
    }
    const handleNavigation = (type: string, id: number) => {
        const baseUrl = 'myactions'

        if (type.replace(' ', '').toLowerCase() === 'wfmabsence') {
            handleWfmAbsenceClick(type, id)
            return
        }

        navigate(resolveToPath(type, id), { state: { previousLocation: `/${baseUrl}` } })
    }

    const getColumnTestId = (columnName: string): string => `MyActions-Chart-${columnName}`
    const handleOnDetailPanelExpandedRowIdsChange = useCallback((newIds: GridRowId[]) => {
        setDetailPanelExpandedRowIds(newIds)
    }, [])
    const getRowClassName = (params: GridRowClassNameParams) =>
        detailPanelExpandedRowIds.includes(params.id) ? 'expanded' : ''

    /**
     * Append panel if requestType is call out
     */
    const getCalloutSummaryPanel = useCallback(({ row }: GridRowParams) => {
        if (mobile) {
            return null
        }
        if (row.requestType === enhancementTypes.callOut) {
            return <CallOutSummary enhancementId={row.id} />
        }
        return null
    }, [mobile])

    /**
     * Desktop View Columns
     */
    let columns: GridColDef[] = [
        {
            field: 'displayName',
            headerName: 'Name',
            headerClassName: 'header',
            flex: 1,
            renderHeader: params => headerText(getColumnTestId(params.field), params.colDef.headerName),
            renderCell: params => (
                <div data-testid={`cell-name-${params.row.id}`}>
                    <Link component="button" variant="body2">
                        {params.row.displayName}
                    </Link>
                </div>
            ),
        },

        {
            field: 'requestType',
            headerName: 'Type',
            headerClassName: 'header',
            flex: 1,
            renderHeader: params => headerText(getColumnTestId(params.field), params.colDef.headerName),
            renderCell: params => (
                <div data-testid={`cell-request-type-${params.row.id}`}>
                    <TypeLabel
                        label={getPalleteTypeByProp(params.row.requestType).label}
                        type={params.row.requestType}
                        testId={`type-chip-${params.row.id}`}
                    />
                </div>
            ),
        },
        {
            field: 'submitDateTime',
            headerName: 'Submitted',
            headerClassName: 'header',
            type: 'date',
            flex: 1,
            renderHeader: params => headerText(getColumnTestId(params.field), params.colDef.headerName),
            renderCell: params => (
                <div data-testid={`cell-submitted-${params.row.id}`}>
                    {getLocalDateString(params.value)}
                </div>
            ),
        },
        {
            field: 'dateFrom',
            headerName: 'Date from',
            headerClassName: 'header',
            type: 'date',
            flex: 1,
            renderHeader: params => headerText(getColumnTestId(params.field), params.colDef.headerName),
            renderCell: params => (
                <div data-testid={`cell-date-from-${params.row.id}`}>
                    {getLocalDateString(params.value)}
                </div>
            ),
        },
        {
            field: 'dateTo',
            headerName: 'Date to',
            headerClassName: 'header',
            type: 'date',
            flex: 1,
            renderHeader: params => headerText(getColumnTestId(params.field), params.colDef.headerName),
            renderCell: params => {
                let displayValue

                // Wfm Absence records represent one day to stop shifts that go past midnight showing as two use dateFrom as dateTo
                if (params.row.requestType === 'Wfm Absence') {
                    displayValue = params.row.dateFrom && getLocalDateString(params.row.dateFrom)
                } else {
                    displayValue = params.value && getLocalDateString(params.value)
                }

                return <div data-testid={`cell-date-to-${params.row.id}`}>{displayValue}</div>
            },
        },
        {
            field: 'hours',
            headerName: 'Hours',
            headerClassName: 'header',
            flex: 1,
            renderHeader: params => headerText(getColumnTestId(params.field), params.colDef.headerName),
            renderCell: params => {
                let displayValue

                if (params.row.requestType === 'Call Out') {
                    displayValue = removeTrailingZeroes(params.row.paidHours)
                } else {
                    displayValue = params.value === 0 ? '-' : removeTrailingZeroes(params.value)
                }

                return <div data-testid={`cell-hours-${params.row.id}`}>{displayValue}</div>
            },
        },
    ]
    const statusColumn: GridColDef[] = [
        {
            field: 'statusDescription',
            headerName: 'Status',
            headerClassName: 'header',
            flex: 1,
            minWidth: 200,
            renderHeader: params => headerText(getColumnTestId(params.field), params.colDef.headerName),
            renderCell: params => (
                <div data-testid={`cell-status-${params.row.id}`}>
                    <StatusLabel status={calculateStatusLabel(params.row)} />
                </div>
            ),
        },
    ]

    if (enhancementsSettingOn) {
        const leftColumns: GridColDef[] = [
            {
                field: 'bulkSubmit',
                headerName: 'bulkSubmit',
                headerClassName: 'header',
                minWidth: 45,
                maxWidth: 45,
                flex: 1,
                disableColumnMenu: true,
                sortable: false,
                renderHeader: params => (
                    <div data-testid="header-bulk-submit">
                        <Checkbox
                            data-testid="header-checkbox-bulk-submit"
                            checked={headerChecked}
                            style={{ margin: '9px 30px 9px 0' }}
                            onChange={event => {
                                if (event.target.checked) {
                                    setHeaderChecked(true)
                                    const sortedRows = apiRef.current.getSortedRows()
                                    const addToArray = (array: number[], id: number) => {
                                        if (!array.includes(id)) array.push(id)
                                    }

                                    sortedRows.forEach(row => {
                                        if (canDecline(row.statusDescription, row.requestType))
                                            addToArray(bulkSumitDeclineRequestIds, row.id)
                                        if (row.requestType === 'Call Out') {
                                            callOutDeclinedCount.current += 1
                                        }
                                        if (canApprove(row.statusDescription, row.requestType))
                                            addToArray(bulkSumitApproveRequestIds, row.id)
                                    })
                                } else {
                                    setHeaderChecked(false)
                                    callOutDeclinedCount.current = 0
                                    setBulkSumitDeclineRequestIds([])
                                    setBulkSumitApproveRequestIds([])
                                }
                                setRerenderCheckBox(rerenderCheckBox + 1)
                            }}
                            disableRipple
                        />
                    </div>
                ),

                renderCell: params => (
                    <div data-testid={`cell-bulk-submit-${params.row.id}`}>
                        {canDecline(params.row.statusDescription, params.row.requestType) &&
                            !['ManAuth'].includes(params.row.requestType) ? (
                            <Checkbox
                                key={rerenderCheckBox}
                                data-testid={`checkbox-bulk-submit-${params.row.id}`}
                                checked={isChecked(params.row.id)}
                                onClick={event => {
                                    event.stopPropagation()
                                }}
                                onChange={event => {
                                    event.stopPropagation()
                                    updateBulkApproveDeclineArrays(
                                        event.target.checked,
                                        params.row.id,
                                        params.row.statusDescription,
                                        params.row.requestType
                                    )
                                    setRerenderCheckBox(rerenderCheckBox + 1)
                                }}
                                style={{ margin: '9px 30px 9px 0' }}
                                disableRipple
                            />
                        ) : (
                            <></>
                        )}
                    </div>
                ),
            },
            {
                ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
                renderCell: params => (
                    <div data-testid={`cell-call-out-toggle-${params.row.id}`}>
                        <RowExpansionToggle id={params.id} value={params.value} />
                    </div>
                ),
            },
        ]
        const isManAuthApprover = (row: any): boolean => {
            if (row.requestType !== 'ManAuth') return false

            const currentEmployeeId = employeeDetails?.employeeId

            return currentEmployeeId !== row.createdById
        }

        const rightColumns: GridColDef[] = [
            {
                field: operationTypes.APPROVE,
                headerName: '',
                headerClassName: 'header',
                flex: 1,
                renderCell: params => (
                    <div data-testid={`cell-approve-decline-${params.row.id}`}>
                        {(params.row.requestType !== 'ManAuth' || isManAuthApprover(params.row)) && (
                            <IconButton
                                data-testid={`button-decline-${params.row.id}`}
                                disabled={!canDecline(params.row.statusDescription, params.row.requestType)}
                                onClick={event => {
                                    event.stopPropagation()
                                    actionsService.userAnalytics
                                        .postEvent('UIClickEvent', 'MyActionsInlineDeclineClick', window.location.pathname, window.location.hostname)
                                        .catch((error: unknown) => console.error('Analytics tracking error:', error))
                                    hasCallOutDeclined.current = params.row.requestType === 'Call Out'
                                    declinedTriggeredBy.current = 'single'
                                    declineRequests([params.row.id])
                                }}
                            >
                                <CloseIcon color="primary" />
                            </IconButton>
                        )}

                        {(params.row.requestType !== 'ManAuth' || isManAuthApprover(params.row)) && (
                            <IconButton
                                data-testid={`button-approve-${params.row.id}`}
                                disabled={!canApprove(params.row.statusDescription, params.row.requestType)}
                                onClick={event => {
                                    event.stopPropagation()
                                    actionsService.userAnalytics
                                        .postEvent('UIClickEvent', 'MyActionsInlineApproveClick', window.location.pathname, window.location.hostname)
                                        .catch((error: unknown) => console.error('Analytics tracking error:', error))
                                    approveRequests([params.row.id])
                                }}
                            >
                                <CheckIcon color="primary" />
                            </IconButton>
                        )}
                    </div>
                ),
            },
        ]
        if (mobile) {
            columns = [...columns, ...statusColumn]
        } else {
            columns = [...leftColumns, ...columns, ...statusColumn, ...rightColumns]
        }
    } else {
        columns = [...columns, ...statusColumn]
    }

    /**
     * Mobile View Columns
     */
    const isSmallScreen = useMediaQuery('(max-width: 767px)')
    const mobileColumns: GridColDef[] = useMemo(() => [
        {
            field: 'displayName',
            headerName: 'Name',
            headerClassName: 'header',
            width: 160,
            minWidth: 160,
            disableColumnMenu: true,
            renderCell: params => (
                <div data-testid={`cell-name-${params.row.id}`}>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={() => handleNavigation(params.row.requestType, params.row.id)}
                    >
                        {params.row.displayName}
                    </Link>
                </div>
            ),
        },

        {
            field: 'requestType',
            headerName: 'Type',
            headerClassName: 'header',
            width: 130,
            minWidth: 130,
            disableColumnMenu: true,
            renderCell: params => (
                <div data-testid={`cell-request-type-${params.row.id}`}>
                    <TypeLabel label={params.row.requestType} type={params.row.requestType} />
                </div>
            ),
        },
        {
            field: 'submitDateTime',
            headerName: 'Submitted',
            headerClassName: 'header',
            width: 130,
            minWidth: 130,
            disableColumnMenu: true,
            type: 'date',

            renderCell: params => (
                <div data-testid={`cell-submitted-${params.row.id}`}>
                    {getLocalDateString(params.value)}
                </div>
            ),
        },
        {
            field: 'dateFrom',
            headerName: 'Date from',
            headerClassName: 'header',
            width: 130,
            minWidth: 130,
            disableColumnMenu: true,
            type: 'date',
            renderCell: params => (
                <div data-testid={`cell-date-from-${params.row.id}`}>
                    {getLocalDateString(params.value)}
                </div>
            ),
        },
        {
            field: 'dateTo',
            headerName: 'Date to',
            headerClassName: 'header',
            width: 130,
            minWidth: 130,
            disableColumnMenu: true,
            type: 'date',
            renderCell: params => (
                <div data-testid={`cell-date-to-${params.row.id}`}>
                    {params.value && getLocalDateString(params.value)}
                </div>
            ),
        },
        {
            field: 'statusDescription',
            headerName: 'Status',
            headerClassName: 'header',
            flex: 1,
            minWidth: isSmallScreen ? 90 : 200,
            disableColumnMenu: true,
            renderCell: params => (
                <div data-testid={`cell-status-${params.row.id}`}>
                    <StatusLabel status={calculateStatusLabel(params.row)} />
                </div>
            ),
        },
    ], [handleNavigation, isSmallScreen])
    const isManager = useSelector<RootStore, string[]>(
        (state: RootStore) => state.userState.permissions
    ).includes('ViewWhoAwayManager')

    const overrideGridComponents: Partial<GridSlotsComponent> | undefined = {
        NoRowsOverlay,
        LoadingOverlay,
    }
    if (enhancementsSettingOn) overrideGridComponents.Footer = CustomGridFooter

    const StyledAproveChip = styled(Chip)`
    background: ${bulkSumitApproveRequestIds.length === 0 ? '#fff' : '#fff'};
    color: ${bulkSumitApproveRequestIds.length === 0 ? '#b5b5b5' : theme.palette.primary.main};
  `

    const StyledDeclineChip = styled(Chip)`
    background: ${bulkSumitDeclineRequestIds.length === 0 ? '#b5b5b5' : '#e75e5e'};
    color: ${bulkSumitDeclineRequestIds.length === 0 ? '#fff' : '#fff'};
  `
    const handleModalConfirmation = () => {
        const { requestIds } = modalStateRef.current

        if (modalStateRef.current.operation === operationTypes.APPROVE) {
            setIsModalLoading(true)
            sendRequestsToApprove(requestIds)
        } else if (comments) {
            setIsModalLoading(true)
            sendRequestsToDecline(requestIds)
        } else {
            setCommentsError(true)
        }
    }

    const textFieldRef = useRef<HTMLInputElement>(null)
    useEffect(() => {
        if (showModal) {
            setTimeout(() => {
                if (textFieldRef.current) {
                    textFieldRef.current.focus()
                }
            }, 100)
        }
    }, [showModal])

    useEffect(() => {
        const wfmAbsenceId = searchParams.get('wfmAbsenceId')

        if (wfmAbsenceId && !isLoading && requestsArray.length > 0) {
            const absenceId = parseInt(wfmAbsenceId, 10)
            const placeholder = requestsArray.find(
                x => x.id === absenceId && x.requestType === 'Wfm Absence'
            )

            if (placeholder) {
                // Use the existing function to handle the WFM absence click
                handleWfmAbsenceClick('Wfm Absence', absenceId)
            }
        }
    }, [searchParams, isLoading, requestsArray])

    return (
        <>
            <Card>
                {enhancementsSettingOn && !mobile ? (
                    <>
                        <Grid
                            item
                            xs={12}
                            md={12}
                            mb={1}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="right"
                            gap={2}
                        >
                            <Box display="flex">
                                <MyActionsFilter
                                    selectionLists={selectionLists}
                                    requestsCount={getFilterTypeCount()}
                                    disabled={isLoading}
                                />
                                <FilterChips />
                            </Box>
                            <Box sx={bulkActionsContainer}>
                                <ButtonSmall
                                    label="Decline Selected"
                                    onClick={() => {
                                        actionsService.userAnalytics
                                            .postEvent('UIClickEvent', 'MyActionsBulkDeclineClick', window.location.pathname, window.location.hostname)
                                            .catch((error: unknown) => console.error('Analytics tracking error:', error))
                                        declinedTriggeredBy.current = 'bulk'
                                        declineRequests(bulkSumitDeclineRequestIds)
                                    }}
                                    disabled={bulkSumitDeclineRequestIds.length === 0}
                                    variant="outlined"
                                    color="error"
                                    sx={approveButtonStyles}
                                    dataTestId="bulk-decline-button"
                                    endIcon={
                                        <StyledDeclineChip
                                            data-testid="bulk-decline-button-counter"
                                            sx={countChipStyles}
                                            label={bulkSumitDeclineRequestIds.length}
                                        />
                                    }
                                />
                                <ButtonSmall
                                    label="Approve Selected"
                                    onClick={() => {
                                        actionsService.userAnalytics
                                            .postEvent('UIClickEvent', 'MyActionsBulkApproveClick', window.location.pathname, window.location.hostname)
                                            .catch((error: unknown) => console.error('Analytics tracking error:', error))
                                        approveRequests(bulkSumitApproveRequestIds)
                                    }}
                                    disabled={bulkSumitApproveRequestIds.length === 0}
                                    variant="outlined"
                                    color="primary"
                                    sx={declineButtonStyles}
                                    dataTestId="bulk-approve-button"
                                    endIcon={
                                        <StyledAproveChip
                                            data-testid="bulk-approve-button-counter"
                                            sx={countChipStyles}
                                            label={bulkSumitApproveRequestIds.length}
                                        />
                                    }
                                />
                            </Box>
                        </Grid>
                        <Modal
                            type="question"
                            open={showModal}
                            onClose={() => {
                                actionsService.userAnalytics
                                    .postEvent('UIClickEvent', 'MyActionsModalCloseClick', window.location.pathname, window.location.hostname)
                                    .catch((error: unknown) => console.error('Analytics tracking error:', error))
                                resetModal()
                            }}
                            onClick={() => {
                                actionsService.userAnalytics
                                    .postEvent('UIClickEvent', 'MyActionsModalConfirmClick', window.location.pathname, window.location.hostname)
                                    .catch((error: unknown) => console.error('Analytics tracking error:', error))
                                handleModalConfirmation()
                            }}
                            title="Are you sure?"
                            message={modalStateRef.current.message}
                            buttonLabel="Confirm"
                            isDisabled={false}
                            buttonLoading={isModalLoading}
                            maxWidth="sm"
                            dropdown={
                                <Grid container>
                                    <Grid item xs={12} md={12} spacing={4}>
                                        <Grid mt={2}>
                                            <TextField
                                                inputRef={textFieldRef}
                                                label="Comments"
                                                value={comments}
                                                fullWidth
                                                multiline
                                                rows={4}
                                                onChange={e => setComments(e.target.value)}
                                                onFocus={() => {
                                                    actionsService.userAnalytics
                                                        .postEvent('UIClickEvent', 'MyActionsModalCommentBoxFocus', window.location.pathname, window.location.hostname)
                                                        .catch((error: unknown) => console.error('Analytics tracking error:', error))
                                                }}
                                                error={commentsError}
                                                helperText={
                                                    modalStateRef.current.operation === operationTypes.APPROVE
                                                        ? ''
                                                        : 'Required'
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            }
                        />
                    </>
                ) : (
                    <>
                        <Box display="flex">
                            <MyActionsFilter
                                selectionLists={selectionLists}
                                requestsCount={getFilterTypeCount()}
                                disabled={isLoading}
                            />
                            <FilterChips />
                        </Box>
                    </>
                )}

                {mobile ? (
                    <Grid container data-testid="my-actions-mobile-view-message">
                        <Grid item xs={12} mb={1}>
                            <Grid item mb={2}>
                                {isManager && doesDisplayInfoMessage && !enhancementsOnly && (
                                    <Alert
                                        onClose={() => {
                                            setDoesDisplayInfoMessage(false)
                                        }}
                                        severity="info"
                                        message="Absence related actions can not be viewed and actioned on mobile devices"
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                ) : (
                    <></>
                )}

                {enhancementsSettingOn ? (
                    <Box sx={{ height: 'calc(100vh - 330px) !important' }}>
                        <SytledMyActionsGrid
                            key={rerenderDataGrid}
                            apiRef={apiRef}
                            getRowId={row => row.id}
                            rows={showLoadingSpinner ? [] : filteredRequests}
                            columns={mobile ? mobileColumns : columns}
                            disableSelectionOnClick
                            rowHeight={50}
                            loading={showLoadingSpinner}
                            components={overrideGridComponents}
                            pinnedColumns={{ left: mobile ? ['displayName'] : [] }}
                            initialState={{
                                sorting: {
                                    sortModel: [{ field: 'submitDateTime', sort: 'asc' }],
                                },
                            }}
                            onRowClick={params => {
                                actionsService.userAnalytics
                                    .postEvent('UIClickEvent', 'MyActionsGridRowClick', window.location.pathname, window.location.hostname)
                                    .catch((error: unknown) => console.error('Analytics tracking error:', error))
                                handleNavigation(params.row.requestType, params.row.id)
                            }}
                            detailPanelExpandedRowIds={detailPanelExpandedRowIds}
                            onDetailPanelExpandedRowIdsChange={handleOnDetailPanelExpandedRowIdsChange}
                            getDetailPanelContent={getCalloutSummaryPanel}
                            getDetailPanelHeight={() => 150}
                            getRowClassName={getRowClassName}
                            componentsProps={{
                                panel: {
                                    sx: {
                                        '& .MuiNativeSelect-select option': {
                                            font: '13px "Poppins", sans-serif',
                                        },
                                    },
                                },
                                footer: {
                                    selectedRows: bulkSumitDeclineRequestIds.length,
                                    testId: 'my-actions-grid-custom-footer',
                                },
                            }}
                            data-testid={getColumnTestId}
                        />
                    </Box>
                ) : (
                    <SytledMyActionsGrid
                        key={rerenderDataGrid}
                        apiRef={apiRef}
                        getRowId={row => row.id}
                        rows={filteredRequests}
                        columns={mobile ? mobileColumns : columns}
                        disableSelectionOnClick
                        pagination
                        autoHeight
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        pageSize={pageSize}
                        rowHeight={50}
                        onPageSizeChange={(newPageSize: number) => setPageSize(newPageSize)}
                        loading={showLoadingSpinner}
                        components={overrideGridComponents}
                        pinnedColumns={{ left: mobile ? ['displayName'] : [] }}
                        initialState={{
                            sorting: {
                                sortModel: [{ field: 'submitDateTime', sort: 'asc' }],
                            },
                        }}
                        onRowClick={params => {
                            actionsService.userAnalytics
                                .postEvent('UIClickEvent', 'MyActionsGridRowClick', window.location.pathname, window.location.hostname)
                                .catch((error: unknown) => console.error('Analytics tracking error:', error))
                            handleNavigation(params.row.requestType, params.row.id)
                        }}
                        detailPanelExpandedRowIds={detailPanelExpandedRowIds}
                        onDetailPanelExpandedRowIdsChange={handleOnDetailPanelExpandedRowIdsChange}
                        getDetailPanelContent={getCalloutSummaryPanel}
                        getDetailPanelHeight={() => 150}
                        getRowClassName={getRowClassName}
                        componentsProps={{
                            panel: {
                                sx: {
                                    '& .MuiNativeSelect-select option': {
                                        font: '13px "Poppins", sans-serif',
                                    },
                                },
                            },
                        }}
                        data-testid={getColumnTestId}
                    />
                )}
            </Card>
            <Drawer
                isOpen={Boolean(wfmAbsencePlaceholder) && showSideDrawer}
                onClose={() => {
                    setWfmAbsencePlaceholder(undefined)
                    dispatch(hideHeaderDrawer())
                    dispatch(hideDrawer())
                }}
                title="Action WFM Absence Placeholder"
                showOptions={false}
            >
                <Box p={4} pt={0}>
                    <WfmAbsenceActionDrawer
                        wfmAbsenceId={wfmAbsencePlaceholder?.id}
                        employeeId={
                            allEmployees.find(
                                e =>
                                    normaliseForComparison(e.employeeName) ===
                                    normaliseForComparison(wfmAbsencePlaceholder?.displayName ?? '')
                            )?.employeeId
                        }
                        refreshActions={async () => {
                            setRequestsArray([])
                            setIsLoading(true)
                            await fetchRequests()
                        }}
                    />
                </Box>
            </Drawer>
            {wfmAbsencePlaceholder && (
                <OpenAbsences
                    openAbsences={openAbsences}
                    open={showOpenAbsenceModal}
                    onClose={resetOpenAbsenceModal}
                    onClick={showDrawerOpenAbsence}
                    wfmAbsenceId={wfmAbsencePlaceholder.id}
                />
            )}
        </>
    )
}

